import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ActivityCounts, BacklogDay } from '@lizrd/contracts';
import type { LineSeries } from '../ui/line-chart';
import { getRecommendationActivity } from '../../lib/api';
import { Card } from '../ui/card';
import { Select } from '../ui/select';
import { SkeletonText } from '../ui/skeleton';
import { InfoButton } from '../ui/info-button';
import { LineChart } from '../ui/line-chart';

type PeriodKey = '30' | '60' | '90' | 'all';
const PERIOD_OPTIONS = [
  { value: '30' as const, label: 'Last 30 days' },
  { value: '60' as const, label: 'Last 60 days' },
  { value: '90' as const, label: 'Last 90 days' },
  { value: 'all' as const, label: 'All time' },
];
const periodDays = (p: PeriodKey) => (p === 'all' ? null : Number(p));

const STAGES: { key: keyof ActivityCounts; label: string; color: string }[] = [
  { key: 'produced', label: 'Produced Recommendations', color: 'var(--info)' },
  { key: 'accepted', label: 'Accepted Recommendations', color: 'var(--warning)' },
  { key: 'executed', label: 'Executed Recommendations', color: 'var(--iris)' },
  { key: 'validated', label: 'Validated Recommendations', color: 'var(--success)' },
];

/** Compact funnel numbers, optionally each as a % of the org total. */
function FunnelRow({ counts, shareOf }: { counts: ActivityCounts; shareOf?: ActivityCounts }) {
  return (
    <div className="flex flex-col divide-y divide-hairline sm:flex-row sm:divide-x sm:divide-y-0">
      {STAGES.map((s) => {
        const n = counts[s.key];
        const total = shareOf?.[s.key];
        const pct = total && total > 0 ? Math.round((n / total) * 100) : null;
        return (
          <div
            key={s.key}
            className="min-w-0 flex-1 px-4 py-2 first:pt-0 sm:first:pl-0 sm:first:pt-2"
          >
            <div className="flex items-center gap-1.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
              <span
                className="inline-block h-1.5 w-1.5 flex-none rounded-full"
                style={{ backgroundColor: s.color }}
              />
              <span className="truncate">{s.label}</span>
            </div>
            <div className="mt-1 font-display text-2xl font-bold tnum" style={{ color: s.color }}>
              {n}
            </div>
            {pct != null && <div className="text-[0.6rem] text-muted">{pct}% of total</div>}
          </div>
        );
      })}
    </div>
  );
}

/** The backlog stock lines — how many sit in each state on each day. */
const STOCK = [
  { key: 'open', label: 'Open', color: 'var(--warning)' },
  { key: 'inProgress', label: 'In progress', color: 'var(--info)' },
  { key: 'validated', label: 'Validated', color: 'var(--success)' },
] as const;

function backlogSeries(backlog: BacklogDay[]): LineSeries[] {
  return STOCK.map((s) => ({
    key: s.key,
    label: s.label,
    color: s.color,
    points: backlog.map((d) => ({ day: d.day, value: d[s.key] })),
  }));
}

const explanation = (
  <>
    <p>
      The top row is what happened in the window — recommendations <b>produced</b> (found),{' '}
      <b>accepted</b> (owned), <b>executed</b> (applied), and <b>validated</b> (proven as savings).
    </p>
    <p>
      The chart is the live <b>backlog</b> — how many are <b>open</b> (waiting), <b>in progress</b>{' '}
      (accepted, not yet proven), and <b>validated</b> on each day. A shrinking open line means the
      team is working the backlog down.
    </p>
  </>
);

/**
 * Cost Optimization Status — the recommendation lifecycle funnel plus an over-time
 * chart, over a selectable window. At org scope it totals across domains with
 * a per-domain filter (each stage as a share of the total); at env scope it
 * shows just that domain. Backed by the recommendation event log.
 */
export function CostOptimizationStatus(
  props: { scope: 'org' } | { scope: 'env'; domainId: string },
) {
  const [period, setPeriod] = useState<PeriodKey>('30');
  const [envFilter, setEnvFilter] = useState<string>('all');
  const days = periodDays(period);
  const isOrg = props.scope === 'org';
  const primaryEnv = isOrg ? undefined : props.domainId;

  const primary = useQuery({
    queryKey: ['rec-activity', days ?? 'all', primaryEnv ?? 'all'],
    queryFn: () => getRecommendationActivity(days, primaryEnv),
    refetchInterval: 60_000,
  });
  // Org + a chosen domain: fetch that env's own series for the chart.
  const secEnv = isOrg && envFilter !== 'all' ? envFilter : undefined;
  const secondary = useQuery({
    queryKey: ['rec-activity', days ?? 'all', secEnv ?? 'all'],
    queryFn: () => getRecommendationActivity(days, secEnv),
    enabled: !!secEnv,
    refetchInterval: 60_000,
  });

  const selected =
    isOrg && envFilter !== 'all'
      ? primary.data?.domains.find((e) => e.domainId === envFilter)
      : null;
  const counts = selected ? selected.total : primary.data?.total;
  const shareOf = selected ? primary.data?.total : undefined;
  const backlog = selected ? secondary.data?.backlog : primary.data?.backlog;
  const chartSeries = useMemo(() => (backlog ? backlogSeries(backlog) : []), [backlog]);
  const hasChart = chartSeries.some((s) => s.points.some((p) => p.value > 0));

  return (
    <Card className="p-5" data-tour="cost-status">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
            Optimization Status
          </h2>
          <InfoButton label="Optimization Status">{explanation}</InfoButton>
        </div>
        <div className="flex items-center gap-2">
          {isOrg && primary.data && (
            <Select
              value={envFilter}
              onChange={setEnvFilter}
              ariaLabel="Domain"
              size="sm"
              options={[
                { value: 'all', label: 'All domains' },
                ...primary.data.domains.map((e) => ({
                  value: e.domainId,
                  label: e.name,
                })),
              ]}
            />
          )}
          <Select
            value={period}
            onChange={(v) => setPeriod(v as PeriodKey)}
            ariaLabel="Time window"
            size="sm"
            options={PERIOD_OPTIONS}
          />
        </div>
      </div>

      {!primary.data || !counts ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : (
        <>
          <FunnelRow counts={counts} shareOf={shareOf} />
          {selected && (
            <Link
              to={`/domains/${selected.domainId}`}
              className="mt-2 inline-block text-xs font-semibold text-iris hover:text-iris-strong"
            >
              View {selected.name} →
            </Link>
          )}
          <div className="mt-4 border-t border-hairline pt-4">
            {selected && secondary.isLoading ? (
              <SkeletonText lines={3} lineClassName="h-4" />
            ) : hasChart ? (
              <>
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
                    Backlog
                  </span>
                  {STOCK.map((s) => (
                    <span key={s.key} className="flex items-center gap-1.5 text-[0.7rem] text-haze">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: s.color }}
                      />
                      {s.label}
                    </span>
                  ))}
                </div>
                <LineChart
                  series={chartSeries}
                  height={200}
                  formatValue={(n) => `${Math.round(n)}`}
                />
              </>
            ) : (
              <div className="grid h-[120px] place-items-center rounded-sm bg-surface-2/40 text-[0.7rem] text-muted">
                No backlog history in this window yet
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
